import {
  HttpUtilService,
  Logger,
  HttpClient,
  HttpResponse,
  CXoneAuthBase,
  UrlUtilsService,
  ApiUriConstants,
  ValidationUtils,
} from '@nice-ccf/core-sdk';
import { AddressBooks, AddressBookEntriesResponse } from '@nice-ccf/common-sdk';
import { AddressBookApiParser } from '../util/address-book-api-parser';
/**
 * This class to expose address book services
 */
export class AddressBookService {
  logger: Logger = new Logger('addressBook', 'AddressBookService');
  private urlUtilSvc = new UrlUtilsService();
  private authBase: CXoneAuthBase;
  private addressBookApiParser = new AddressBookApiParser();
  protected utilService: HttpUtilService = new HttpUtilService();
  private validationUtils = new ValidationUtils();
  /**
   *
   * ```
   * @example
   * const adminService = new AdminService();
   * ```
   */
  constructor() {
    this.authBase = CXoneAuthBase.Instance;
  }
  /**
   * This method to get all address books for selected agentId
   * @param agentId -  agent id
   * @example
   * ```
   * getAllAddressBooks('23423423');
   * ```
   */
  public getAllAddressBooks(agentId?: string, includeEntries = false): Promise<AddressBooks[]> {
    const requestParams = {
      includeEntries: includeEntries,
    };
    const cxOneConfig = this.authBase.getCXoneConfig();
    const user = this.authBase.getUserDetails();
    agentId = agentId ? agentId : user.icAgentId;
    const addressBooksUrl = ApiUriConstants.ADDRESS_BOOK_URI.replace('{agentId}', agentId);
    const url = cxOneConfig.acdApiBaseUri + this.urlUtilSvc.appendQueryString(addressBooksUrl, requestParams);
    const token = this.authBase.getAuthToken();
    const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
    return new Promise((resolve, reject) => {
      if (this.validationUtils.isNotNullOrEmpty(agentId)) {
        HttpClient.get(url, reqInit).then(
          (response: HttpResponse) => {
            this.logger.info('getAllAddressBooks', 'Get Address Books Success');
            let addressBookList: AddressBooks[] = [];
            addressBookList = this.addressBookApiParser.parseAddressBookList(response);
            resolve(addressBookList);
          },
          (error) => {
            this.logger.error('getAllAddressBooks', 'Get Address Books failed:-' + error.toString());
            reject(error);
          }
        );
      } else {
        this.logger.error('getAllAddressBooks', 'agentId is empty');
        reject('agentId is empty');
      }
    });
  }

  /**
   * This method to get standard address book entries
   * @param addressBookId - address book id
   * @param startIndex - starting index for address book list
   * @param recordsToLoad -  number of records to load
   * @param searchText - search text for address book
   * @example
   * ```
   * getStandardEntries('23423423');
   * ```
   * @returns
   */
  getStandardEntries(
    addressBookId: string,
    startIndex = 0,
    recordsToLoad = 50,
    searchText = ''
  ): Promise<AddressBookEntriesResponse> {
    const requestParams = {
      updatedSince: new Date(0).toISOString(),
      skip: startIndex,
      top: recordsToLoad,
      searchString: searchText,
    };
    const cxOneConfig = this.authBase.getCXoneConfig();
    const standardAddressBookUrl = ApiUriConstants.STANDARD_ADDRESS_BOOK_URI.replace('{addressBookId}', addressBookId);
    const url = cxOneConfig.acdApiBaseUri + this.urlUtilSvc.appendQueryString(standardAddressBookUrl, requestParams);
    const token = this.authBase.getAuthToken();
    const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
    return new Promise((resolve, reject) => {
      HttpClient.get(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('getStandardEntries', 'Get Standard Address Books Success');
          const formattedResponse = this.addressBookApiParser.parseStandardAddressBookEntries(response);
          resolve(formattedResponse);
        },
        (error) => {
          this.logger.error('getStandardEntries', 'Get Standard Address Books Failed-' + error.toString());
          reject(error);
        }
      );
    });
  }
}
